import { useMutation, useQueryClient } from "@tanstack/react-query";
import axiosAuth from "../../axiosAuth";
import { SCHOOL_LAB_POC_CREATE, SCHOOL_LAB_RESOURCES_CREATE } from "../apiRoutes";

// Adjusted to accept the POC payload shape used elsewhere in the app:
// {
//   schoolId: string,
//   pocList: [{ pocName, pocPhone, pocEmail, pocDesignation }]
// }
export interface CreateLabPocDetails {
  schoolId: string;
  pocList: {
    pocName: string;
    pocPhone: string;
    pocEmail: string;
    pocDesignation: string;
  }[];
}

export interface CreateLabPocDetailsResponse {
  data: any; // keep flexible — API returns a created resource object
}

// Define the request type for creating lab POC details
export type CreateCrmClassRequest = CreateLabPocDetails;

interface UseCreateLabPocDetailsMutationProps {
  onSuccess?: (data: CreateLabPocDetailsResponse) => void;
  onError?: (error: any) => void;
}

const createLabPocDetails = async (data: CreateCrmClassRequest): Promise<CreateLabPocDetailsResponse> => {
  const response = await axiosAuth.post<CreateLabPocDetailsResponse>(SCHOOL_LAB_POC_CREATE, data);
  return response.data;
};

export const useCreateLabPocDetailsMutation = ({
  onSuccess,
  onError,
}: UseCreateLabPocDetailsMutationProps = {}) => {
  const queryClient = useQueryClient();

  return useMutation<CreateLabPocDetailsResponse, Error, CreateCrmClassRequest>({
    mutationFn: createLabPocDetails,
    onSuccess: (data) => {
      // Invalidate and refetch CRM class data
      queryClient.invalidateQueries({ queryKey: ["crm-class"] });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

// Create lab resources (resourceList: [{ schoolId, profile_id, isDeleted }])
export const useCreateLabResourcesMutation = ({
  onSuccess,
  onError,
}: UseCreateLabPocDetailsMutationProps = {}) => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, { resourceList: any[] }>({
    mutationFn: async (data) => {
      const response = await axiosAuth.post(SCHOOL_LAB_RESOURCES_CREATE, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["schoolLabResource"] });
      onSuccess?.(data);
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};
